import { works, topics, type Work, type Topic } from '@/mocks/movies';

export type SortKey = 'latest' | 'hot' | 'rating';

export interface WorkFilter {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
  sort?: SortKey;
}

export function getWorkById(id: string): Work | undefined {
  return works.find((item) => item.id === id);
}

export function getTopicById(id: string): Topic | undefined {
  return topics.find((item) => item.id === id);
}

export function getWorksByType(type: string): Work[] {
  return works.filter((item) => item.type === type);
}

export function getFeaturedWorks(): Work[] {
  return works.filter((item) => item.featured).sort((a, b) => b.views - a.views);
}

export function getHotWorks(limit = 8): Work[] {
  return [...works].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestWorks(limit = 8): Work[] {
  return works.filter((item) => item.latest).sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getTopRatedWorks(limit = 6): Work[] {
  return [...works].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getWorksByTopic(topic: Topic): Work[] {
  return topic.workIds
    .map((id) => getWorkById(id))
    .filter((item): item is Work => Boolean(item));
}

export function getRelatedWorks(work: Work, limit = 6): Work[] {
  const scored = works
    .filter((item) => item.id !== work.id)
    .map((item) => {
      let score = 0;
      if (item.type === work.type) score += 3;
      score += item.genres.filter((g) => work.genres.includes(g)).length * 4;
      score += item.topics.filter((t) => work.topics.includes(t)).length * 2;
      if (item.region === work.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function filterWorks(filter: WorkFilter): Work[] {
  let result = works.filter((item) => {
    if (filter.type && filter.type !== 'all' && item.type !== filter.type) return false;
    if (filter.genre && filter.genre !== '全部' && !item.genres.includes(filter.genre)) return false;
    if (filter.region && filter.region !== '全部' && item.region !== filter.region) return false;
    if (filter.year && filter.year !== '全部' && String(item.year) !== filter.year) return false;
    return true;
  });

  const sort = filter.sort ?? 'latest';
  result = [...result].sort((a, b) => {
    if (sort === 'hot') return b.views - a.views;
    if (sort === 'rating') return b.rating - a.rating;
    return b.year - a.year;
  });

  return result;
}

export function searchWorks(keyword: string): Work[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return works.filter((item) => {
    const haystack = [
      item.title,
      item.subtitle,
      item.typeLabel,
      item.region,
      item.director,
      item.synopsis,
      item.story,
      ...item.genres,
      ...item.cast,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return String(views);
}

export const FAVORITES_KEY = 'changri_favorites';

export function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

export function toggleFavorite(id: string): string[] {
  const current = readFavorites();
  const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
  return next;
}